
import { useState, useEffect } from 'react';

export default function RoofCalculator() {
  const [propertyType, setPropertyType] = useState<'residential' | 'commercial' | 'industrial' | 'multiUnit'>('residential');
  const [squareFootage, setSquareFootage] = useState(2500);
  const [serviceType, setServiceType] = useState<'visual' | 'thermal' | 'full'>('visual');
  const [roofComplexity, setRoofComplexity] = useState<'simple' | 'moderate' | 'complex'>('simple');
  const [priorityTurnaround, setPriorityTurnaround] = useState(false);
  const [outsideMobilization, setOutsideMobilization] = useState(false);

  const [totalLow, setTotalLow] = useState(0);
  const [totalHigh, setTotalHigh] = useState(0);

  useEffect(() => {
    calculatePricing();
  }, [propertyType, squareFootage, serviceType, roofComplexity, priorityTurnaround]);

  const calculatePricing = () => {
    // Base prices by property type
    let baseLow = 0;
    let baseHigh = 0;

    switch (propertyType) {
      case 'residential':
        baseLow = 150;
        baseHigh = 350;
        break;
      case 'commercial':
        baseLow = 300;
        baseHigh = 800;
        break;
      case 'industrial':
        baseLow = 500;
        baseHigh = 1500;
        break;
      case 'multiUnit':
        baseLow = 400;
        baseHigh = 1200;
        break;
    }

    // Service type multiplier
    let serviceMultiplier = 1.0;
    switch (serviceType) {
      case 'visual':
        serviceMultiplier = 1.0;
        break;
      case 'thermal':
        serviceMultiplier = 1.5;
        break;
      case 'full':
        serviceMultiplier = 2.0;
        break;
    }

    // Roof complexity multiplier
    let complexityMultiplier = 1.0;
    switch (roofComplexity) {
      case 'simple':
        complexityMultiplier = 1.0;
        break;
      case 'moderate':
        complexityMultiplier = 1.2;
        break;
      case 'complex':
        complexityMultiplier = 1.5;
        break;
    }

    // Size adjustment (per 1000 sq ft over base)
    const baseSize = propertyType === 'residential' ? 2000 : propertyType === 'commercial' ? 10000 : propertyType === 'industrial' ? 25000 : 5000;
    const sizeOverBase = Math.max(0, squareFootage - baseSize);
    const sizeIncrements = sizeOverBase / 1000;
    
    let sizeAdder = 0;
    switch (propertyType) {
      case 'residential':
        sizeAdder = sizeIncrements * 25;
        break;
      case 'commercial':
        sizeAdder = sizeIncrements * 15;
        break;
      case 'industrial':
        sizeAdder = sizeIncrements * 10;
        break;
      case 'multiUnit':
        sizeAdder = sizeIncrements * 20;
        break;
    }

    // Calculate totals
    let low = (baseLow + sizeAdder) * serviceMultiplier * complexityMultiplier;
    let high = (baseHigh + sizeAdder) * serviceMultiplier * complexityMultiplier;

    // Apply priority turnaround
    if (priorityTurnaround) {
      low *= 1.2;
      high *= 1.2;
    }

    setTotalLow(Math.round(low));
    setTotalHigh(Math.round(high));
  };

  const handleSquareFootageChange = (value: number) => {
    const maxSize = propertyType === 'residential' ? 10000 : propertyType === 'commercial' ? 100000 : propertyType === 'industrial' ? 500000 : 50000;
    const minSize = propertyType === 'residential' ? 500 : propertyType === 'commercial' ? 1000 : propertyType === 'industrial' ? 5000 : 2000;
    const clampedValue = Math.max(minSize, Math.min(maxSize, value));
    setSquareFootage(clampedValue);
  };

  const getMaxSize = () => {
    switch (propertyType) {
      case 'residential': return 10000;
      case 'commercial': return 100000;
      case 'industrial': return 500000;
      case 'multiUnit': return 50000;
    }
  };

  const getMinSize = () => {
    switch (propertyType) {
      case 'residential': return 500;
      case 'commercial': return 1000;
      case 'industrial': return 5000;
      case 'multiUnit': return 2000;
    }
  };

  const getPropertyDescription = () => {
    switch (propertyType) {
      case 'residential':
        return 'Single-family homes, townhouses, and small residential properties.';
      case 'commercial':
        return 'Office buildings, retail spaces, restaurants, and commercial properties.';
      case 'industrial':
        return 'Warehouses, manufacturing facilities, and large industrial complexes.';
      case 'multiUnit':
        return 'Apartment buildings, condos, and multi-family residential properties.';
    }
  };

  // Reset square footage when property type changes
  useEffect(() => {
    switch (propertyType) {
      case 'residential':
        setSquareFootage(2500);
        break;
      case 'commercial':
        setSquareFootage(15000);
        break;
      case 'industrial':
        setSquareFootage(50000);
        break;
      case 'multiUnit':
        setSquareFootage(10000);
        break;
    }
  }, [propertyType]);

  return (
    <div id="roof-calculator" className="bg-white rounded-lg shadow-sm border border-stone-200 p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-stone-900 mb-3">Roof &amp; Property Inspection Estimator</h2>
        <p className="text-stone-600">
          Select your property type and size for an instant estimate. Pricing varies by inspection type and roof complexity.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Calculator Inputs */}
        <div className="space-y-6">
          {/* Property Type */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Property Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="propertyType"
                  value="residential"
                  checked={propertyType === 'residential'}
                  onChange={() => setPropertyType('residential')}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Residential</span>
                  <p className="text-xs text-stone-500">Single-family, townhouse</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="propertyType"
                  value="commercial"
                  checked={propertyType === 'commercial'}
                  onChange={() => setPropertyType('commercial')}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Commercial</span>
                  <p className="text-xs text-stone-500">Office, retail, restaurant</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="propertyType"
                  value="industrial"
                  checked={propertyType === 'industrial'}
                  onChange={() => setPropertyType('industrial')}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Industrial</span>
                  <p className="text-xs text-stone-500">Warehouse, manufacturing</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="propertyType"
                  value="multiUnit"
                  checked={propertyType === 'multiUnit'}
                  onChange={() => setPropertyType('multiUnit')}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Multi-Unit Residential</span>
                  <p className="text-xs text-stone-500">Apartments, condos</p>
                </div>
              </label>
            </div>
          </div>

          {/* Square Footage */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Roof Area (Square Feet)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={getMinSize()}
                max={getMaxSize()}
                step={propertyType === 'residential' ? 100 : 1000}
                value={squareFootage}
                onChange={(e) => handleSquareFootageChange(Number(e.target.value))}
                className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
              />
              <input
                type="number"
                min={getMinSize()}
                max={getMaxSize()}
                value={squareFootage}
                onChange={(e) => handleSquareFootageChange(Number(e.target.value))}
                className="w-28 px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <p className="text-xs text-stone-500 mt-2">
              {getMinSize().toLocaleString()} to {getMaxSize().toLocaleString()} sq ft for {propertyType} properties
            </p>
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Inspection Type
            </label>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value as 'visual' | 'thermal' | 'full')}
              className="w-full px-4 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="visual">Visual Inspection (RGB Photos)</option>
              <option value="thermal">Thermal Inspection (Infrared)</option>
              <option value="full">Full Diagnostic (Visual + Thermal + Report)</option>
            </select>
          </div>

          {/* Roof Complexity */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Roof Complexity
            </label>
            <select
              value={roofComplexity}
              onChange={(e) => setRoofComplexity(e.target.value as 'simple' | 'moderate' | 'complex')}
              className="w-full px-4 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="simple">Simple (Flat or single-slope)</option>
              <option value="moderate">Moderate (Multiple sections, some features)</option>
              <option value="complex">Complex (Many angles, dormers, skylights, equipment)</option>
            </select>
          </div>

          {/* Add-ons */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Add-ons
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="checkbox"
                  checked={priorityTurnaround}
                  onChange={(e) => setPriorityTurnaround(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                />
                <span className="text-sm font-medium text-stone-900">Priority turnaround (24-48 hours): +20%</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="checkbox"
                  checked={outsideMobilization}
                  onChange={(e) => setOutsideMobilization(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-stone-900">Outside service area</span>
                  <p className="text-xs text-stone-500 mt-1">Travel billed separately</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="bg-stone-50 rounded-lg p-6 border border-stone-200 h-fit">
          <h3 className="text-xl font-bold text-stone-900 mb-4">Estimated Pricing</h3>
          
          <div className="bg-amber-50 rounded-lg p-3 mb-6 border border-amber-200">
            <p className="text-sm text-amber-800">{getPropertyDescription()}</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Property Type</span>
              <span className="text-lg font-bold text-stone-900 capitalize">{propertyType === 'multiUnit' ? 'Multi-Unit' : propertyType}</span>
            </div>
            
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Roof Area</span>
              <span className="text-lg font-bold text-stone-900">{squareFootage.toLocaleString()} sq ft</span>
            </div>
            
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Inspection Type</span>
              <span className="text-lg font-bold text-stone-900 capitalize">
                {serviceType === 'full' ? 'Full Diagnostic' : serviceType}
              </span>
            </div>
            
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="text-sm font-medium text-amber-800 mb-2">Estimated Price Range</div>
              <div className="text-3xl font-bold text-amber-900">
                ${totalLow.toLocaleString()} to ${totalHigh.toLocaleString()}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-stone-600 mb-6">
            Final pricing depends on roof access, specific conditions, and reporting requirements.
          </p>
          
          <div className="space-y-3">
            <a
              href="/instant-quote"
              className="block w-full bg-amber-700 text-white text-center px-6 py-3 rounded-md font-semibold hover:bg-amber-800 transition-colors whitespace-nowrap"
            >
              Get a Firm Quote
            </a>
            <a
              href="tel:+1234567890"
              className="block w-full bg-white text-stone-900 text-center px-6 py-3 rounded-md font-semibold border border-stone-300 hover:bg-stone-50 transition-colors whitespace-nowrap"
            >
              Call for Details
            </a>
          </div>
        </div>
      </div>

      {/* What You Get Panel */}
      <div className="mt-10 pt-8 border-t border-stone-200">
        <h3 className="text-xl font-bold text-stone-900 mb-6">What You Get</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-camera-line text-xl text-amber-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Visual Inspection</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• High-res photo set</li>
              <li>• Damage identification</li>
              <li>• Annotated findings</li>
              <li>• Condition summary</li>
            </ul>
          </div>
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-temp-hot-line text-xl text-amber-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Thermal Inspection</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• Infrared imagery</li>
              <li>• Moisture detection</li>
              <li>• Insulation gaps</li>
              <li>• Heat loss mapping</li>
            </ul>
          </div>
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-file-chart-line text-xl text-amber-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Full Diagnostic</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• Visual + thermal data</li>
              <li>• Priority issue ranking</li>
              <li>• Executive summary PDF</li>
              <li>• Repair recommendations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
